import { ArrowRight, Search } from "lucide-react";
import { CarCard, CarCardSkeleton } from "@/components/cars/CarCard";
import { SearchStateLink } from "@/components/search/SearchStateLink";
import { useSearchCars } from "@/hooks/useCars";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import type { GeoListingsQuery } from "@/content/geo";

interface GeoListingsProps {
  query: GeoListingsQuery;
  heading?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export function GeoListings({ query, heading, ctaLabel, ctaHref }: GeoListingsProps) {
  const { t } = useAppTranslation();
  const resolvedHeading = heading ?? t("geo.live_listings");
  const resolvedCtaLabel = ctaLabel ?? t("geo.browse_all_matching");
  // GeoListingsQuery is intentionally a subset of SearchParams; pass through
  // directly so backend filters (make / location / rental_type / etc.) are
  // honoured. See task #2383 review feedback re: unsupported keys.
  const { data, isLoading } = useSearchCars({ ...query, pageSize: 8 });

  return (
    <section className="bg-background py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="font-sans text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {resolvedHeading}
            </h2>
            {data && data.total > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">
                {t("geo.cars_available", { count: data.total })}
              </p>
            )}
          </div>
          <SearchStateLink
            searchPath={ctaHref || "/search"}
            state={query}
            className="hidden items-center gap-1 text-sm font-medium text-primary hover:underline sm:flex"
          >
            {resolvedCtaLabel}
            <ArrowRight className="h-4 w-4" />
          </SearchStateLink>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => <CarCardSkeleton key={i} />)
            : (data?.cars || []).map((car) => <CarCard key={car.id} car={car} />)}
          {!isLoading && (!data?.cars || data.cars.length === 0) && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {t("geo.no_live_listings")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("geo.new_cars_weekly")}
              </p>
              <SearchStateLink
                state={{}}
                className="cursor-pointer mt-4 inline-flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
              >
                {t("geo.browse_all_cars")}
                <ArrowRight className="h-4 w-4" />
              </SearchStateLink>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
